import { createApi } from "@reduxjs/toolkit/query/react";
import {
  SkuReports,
  SkuReportsResult,
  SkuReportsSearch,
} from "@/types/flipkartAllCompany";
import {
  OrderItemSearch,
  OrderItemSearchResult,
  OrderItemSummaryResult,
  MissingStockSearch,
  MissingStockResult,
} from "@/types/OrderItem";
import { marketPlaceBaseQueryWrapper } from "@/utils/rtkHelper";
import {
  UnAssignSkuSearch,
  UnAssignSkuSearchResult,
  UnAssignSku,
} from "@/types/UnAssignSku";
// import { useExportFlipkartOrdersMutation } from "./orderItem";
// import { useSkuReportsFlipkartOrdersQuery } from "./orderItem";

export const FlipkartAllCompanyApi = createApi({
  reducerPath: "FlipkartAllCompanyApi",
  baseQuery: marketPlaceBaseQueryWrapper,
  keepUnusedDataFor: 0,
  tagTypes: ["flipkart-all-company"],
  endpoints: (builder) => ({
    getDashboardCounts: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/dashboard-count/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getTotalOrdersChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/total-order-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getChargeableAmtChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/chargeable-amounts/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getReturnStatusChartChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/return-status-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getTop10SkuProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/top-10-sku-profit-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getTop10SkuLossChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/top-10-sku-loss-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getTop10SalesSkuChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/top-10-sales-sku/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getTop10StateWiseCustomerReturnChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/top-10-customer-returns-state/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getTop10SkuOfCustomerReturnChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/top-10-sku-of-customer-return/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getTop10CityWiseSalesOrderChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/top-10-sales-order-city-wise/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getTop10StateWiseSalesOrderChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/top-10-sales-order-state-wise/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getTop10CustomerReturnChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/top-10-customer-return/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getRealTimeProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/real-time-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getUpcomingProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/upcoming-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    getNetProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/flipkart-all-company/net-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["flipkart-all-company"],
    }),
    skuReportsFlipkartOrders: builder.query<SkuReportsResult, SkuReportsSearch>(
      {
        query: (input) => ({
          url: `/flipkart-all-company/sku-report`,
          method: "POST",
          body: { ...input, export: false },
        }),
        providesTags: ["flipkart-all-company"],
      },
    ),
    summarySkuReport: builder.query<SkuReports, SkuReportsSearch>({
      query: (input) => ({
        url: `/flipkart-all-company/sku-report/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["flipkart-all-company"],
    }),
    exportSkuReports: builder.mutation<Blob, SkuReportsSearch>({
      query: (input) => ({
        url: `/flipkart-all-company/sku-report`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    getDropdownOptions: builder.query({
      query: () => `/flipkart-all-company/sku/sku-dropdown`,
      providesTags: ["flipkart-all-company"],
    }),
    searchFlipkartOrders: builder.query<OrderItemSearchResult, OrderItemSearch>(
      {
        query: (input) => ({
          url: `/flipkart-all-company/search`,
          method: "POST",
          body: { ...input, export: false },
        }),
        providesTags: ["flipkart-all-company"],
      },
    ),
    exportFlipkartOrders: builder.mutation<Blob, OrderItemSearch>({
      query: (input) => ({
        url: `/flipkart-all-company/search`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    summaryOrderItem: builder.query<OrderItemSummaryResult, OrderItemSearch>({
      query: (input) => ({
        url: `/flipkart-all-company/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["flipkart-all-company"],
    }),
    getUnAssignSku: builder.query<UnAssignSkuSearchResult, UnAssignSkuSearch>({
      query: (input) => ({
        url: `/flipkart-all-company/unassigned-skus`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["flipkart-all-company"],
    }),
    exportUnAssignSku: builder.mutation<Blob, UnAssignSkuSearch>({
      query: (input) => ({
        url: `/flipkart-all-company/unassigned-skus`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
  }),
});
export const {
  useGetDashboardCountsQuery,
  useGetTotalOrdersChartQuery,
  useGetReturnStatusChartChartQuery,
  useGetChargeableAmtChartQuery,
  useGetTop10SkuProfitChartQuery,
  useGetTop10SkuLossChartQuery,
  useGetTop10SalesSkuChartQuery,
  useGetTop10StateWiseCustomerReturnChartQuery,
  useGetTop10SkuOfCustomerReturnChartQuery,
  useGetTop10CityWiseSalesOrderChartQuery,
  useGetTop10StateWiseSalesOrderChartQuery,
  useGetTop10CustomerReturnChartQuery,
  useGetNetProfitChartQuery,
  useGetRealTimeProfitChartQuery,
  useGetUpcomingProfitChartQuery,
  useGetDropdownOptionsQuery,
  useSkuReportsFlipkartOrdersQuery,
  useSummarySkuReportQuery,
  useExportSkuReportsMutation,
  useExportFlipkartOrdersMutation,
  useSearchFlipkartOrdersQuery,
  useSummaryOrderItemQuery,
  useGetUnAssignSkuQuery,
  useExportUnAssignSkuMutation,
} = FlipkartAllCompanyApi;
